import { TargetingFilterType } from '@gsc/types'
import Emitter from '../../../shared/emitter'
import { Visit } from '../../session'
import { activityWatcher } from './activity-watcher'
import { bounceWatcher } from './bounce-watcher'
import { scrollWatcher } from './scroll-watcher'
import { AjaxRequest, AjaxRequestFilter, ajaxWatcher } from './ajax-watcher'

const externalTimerKeys = [
  TargetingFilterType.CssSelector,
  TargetingFilterType.Cookie,
  TargetingFilterType.TimeDisplay,
  TargetingFilterType.TimeSinceAction,
]

const timerKeys = [
  TargetingFilterType.ScrollPercent,
  TargetingFilterType.TimeOnPage,
  TargetingFilterType.TimeOnSite,
  TargetingFilterType.TimeIdle,
  ...externalTimerKeys,
]

const allKeys = [TargetingFilterType.ExitAttempted, ...timerKeys]

const defaultConfig = {
  timerEnabled: false,
  timerResolution: 500, //ms
  updateByTimer: false,

  runBounce: false,
  runActivity: false,
  runScroll: false,
}

interface Value {
  [TargetingFilterType.ExitAttempted]?: boolean
  [TargetingFilterType.ScrollPercent]?: number
  [TargetingFilterType.TimeOnSite]?: number
  [TargetingFilterType.TimeOnPage]?: number
  [TargetingFilterType.TimeIdle]?: number
}

const watches = {
  exitAttempted: false,
  lastActivity: new Date(),
  scrollPercent: 0,
}

export default class Watchers extends Emitter {
  private options: typeof defaultConfig = { ...defaultConfig }
  private enabledKeys: { [key: string]: boolean } = {}
  private watches: typeof watches = watches
  private ajaxWatchers: string[] = []

  private prevValue: Value = null

  private visit: Visit

  constructor(visit: Visit, keys: TargetingFilterType[]) {
    super()

    this.configure(keys)

    this.reset(visit)

    // configure watchers
    if (this.options.runBounce) {
      bounceWatcher(() => {
        this.watches.exitAttempted = true
        this.update() // force update
      })
    }

    if (this.options.runScroll) {
      scrollWatcher(scrollPercent => {
        this.watches.scrollPercent = scrollPercent
      })
    }

    if (this.options.runActivity) {
      activityWatcher((activity: string) => {
        this.watches.lastActivity = new Date()
      })
    }

    // setup timer
    if (this.options.timerEnabled) {
      setInterval(() => this.update(), this.options.timerResolution)
    }
  }

  private configure(keys: TargetingFilterType[]) {
    keys.forEach(k => {
      if (allKeys.indexOf(k) !== -1) {
        this.enabledKeys[k] = true

        // configure timer
        if (timerKeys.indexOf(k) !== -1) {
          this.options.timerEnabled = true
          if (externalTimerKeys.indexOf(k) !== -1) {
            this.options.updateByTimer = true
          }
        }

        //configure watchers
        if (k === TargetingFilterType.TimeIdle) {
          this.options.runActivity = true
        } else if (k === TargetingFilterType.ScrollPercent) {
          this.options.runScroll = true
        } else if (k === TargetingFilterType.ExitAttempted) {
          this.options.runBounce = true
        }
      }
    })
  }

  private update(force = false) {
    const value = this.currentValue
    if (
      force ||
      this.options.updateByTimer ||
      !this.prevValue ||
      Object.keys(this.enabledKeys).some((key: keyof Value) => this.prevValue[key] !== value[key])
    ) {
      this.prevValue = value
      console.log('watchers update')
      this.trigger('change', value)
    }
  }

  public reset(visit: Visit): void {
    this.prevValue = {}
    this.visit = visit
    this.watches = {
      ...watches,
      scrollPercent: this.watches.scrollPercent, // keep saved scroll
    }
  }

  public readValue(): Value {
    const result = this.currentValue

    // reset
    if (this.watches.exitAttempted) {
      this.prevValue[TargetingFilterType.ExitAttempted] = this.watches.exitAttempted = false
    }
    console.log(this.watches)

    return result
  }

  get currentValue(): Value {
    const now = Date.now()
    const value: Value = {}
    Object.keys(this.enabledKeys).forEach(key => {
      if (key === TargetingFilterType.ExitAttempted) {
        value[key] = this.watches.exitAttempted
      } else if (key === TargetingFilterType.ScrollPercent) {
        value[key] = this.watches.scrollPercent
      } else if (key === TargetingFilterType.TimeIdle) {
        value[key] = now - this.watches.lastActivity.getTime()
      } else if (key === TargetingFilterType.TimeOnPage) {
        value[key] = now - this.visit.time
      } else if (key === TargetingFilterType.TimeOnSite) {
        value[key] = now - this.visit.sessionStartTime
      }
    })

    return value
  }

  public addAjaxWatcher(filter: AjaxRequestFilter, callback: (req: AjaxRequest) => any, retargeting = false) {
    const handler = (req: AjaxRequest) => {
      if (callback) {
        callback(req)
      }
      this.update(retargeting)
    }
    const key = `${filter.method}: ${filter.url}`
    if (this.ajaxWatchers.indexOf(key) === -1) {
      ajaxWatcher(handler, filter)
      this.ajaxWatchers.push(key)
    }
  }
}
